import {
  EmptyEnrollmentApplication,
  EnrollmentApplication,
  EnrollmentApplicationWithError,
  EnrollmentType,
  SavingEnrollmentApplication,
  ValidEnrollmentApplication,
} from 'Agent/Accounts/models/EnrollmentApplication';
import {createAction} from 'Shared/utilities/actionCreator';

export enum EnrollmentApplicationActionType {
  UPLOAD_APPLICATION = 'enrollmentApplication/UPLOAD_APPLICATION',
  CHANGE_ENROLLMENT_TYPE = 'enrollmentApplication/CHANGE_ENROLLMENT_TYPE',
  CHANGE_ENROLLMENT_FILE = 'enrollmentApplication/CHANGE_ENROLLMENT_FILE',
  CHANGE_SERVICING_AGENT_NUMBER = 'enrollmentApplication/CHANGE_SERVICING_AGENT_NUMBER',
  SHOW_ERROR = 'enrollmentApplication/SHOW_ERROR',
  CLEAR_APPLICATION = 'enrollmentApplication/CLEAR_APPLICATION',
  CHANGE_ACCOUNT_NUMBER = 'enrollmentApplication/CHANGE_ACCOUNT_NUMBER',
}

export const showError = (application: ValidEnrollmentApplication) =>
  createAction(EnrollmentApplicationActionType.SHOW_ERROR, {application});
export const uploadApplication = (
  accountId: string,
  application: ValidEnrollmentApplication,
  encryptedIdentifier: string
) => createAction(EnrollmentApplicationActionType.UPLOAD_APPLICATION, {accountId, application, encryptedIdentifier});
export const changeServicingAgentNumber = (servicingAgentNumber: string) =>
  createAction(EnrollmentApplicationActionType.CHANGE_SERVICING_AGENT_NUMBER, {servicingAgentNumber});
export const changeAccountNumber = (accountNumber: string) =>
  createAction(EnrollmentApplicationActionType.CHANGE_ACCOUNT_NUMBER, {accountNumber});
export const clearApplication = () => createAction(EnrollmentApplicationActionType.CLEAR_APPLICATION);
export const changeEnrollmentType = (enrollmentType: EnrollmentType) =>
  createAction(EnrollmentApplicationActionType.CHANGE_ENROLLMENT_TYPE, {enrollmentType});
export const changeEnrollmentFile = (enrollmentFile: File) =>
  createAction(EnrollmentApplicationActionType.CHANGE_ENROLLMENT_FILE, {enrollmentFile});

type EnrollmentApplicationState = {
  application: EnrollmentApplication;
};

const DEFAULT_STATE: EnrollmentApplicationState = {
  application: EmptyEnrollmentApplication,
};

type EnrollmentApplicationAction =
  | ReturnType<typeof showError>
  | ReturnType<typeof changeEnrollmentFile>
  | ReturnType<typeof changeEnrollmentType>
  | ReturnType<typeof changeServicingAgentNumber>
  | ReturnType<typeof uploadApplication>
  | ReturnType<typeof clearApplication>
  | ReturnType<typeof changeAccountNumber>;

export default (
  state: EnrollmentApplicationState = DEFAULT_STATE,
  action: EnrollmentApplicationAction
): EnrollmentApplicationState => {
  switch (action.type) {
    case EnrollmentApplicationActionType.SHOW_ERROR:
      return {
        ...state,
        application: new EnrollmentApplicationWithError(
          action.payload.application.accountNumber,
          action.payload.application.enrollmentType,
          action.payload.application.servicingAgentNumber,
          action.payload.application.file
        ),
      };
    case EnrollmentApplicationActionType.UPLOAD_APPLICATION:
      return {
        ...state,
        application: new SavingEnrollmentApplication(
          action.payload.application.accountNumber,
          action.payload.application.enrollmentType,
          action.payload.application.servicingAgentNumber,
          action.payload.application.file
        ),
      };
    case EnrollmentApplicationActionType.CLEAR_APPLICATION:
      return {
        ...state,
        application: EmptyEnrollmentApplication,
      };
    case EnrollmentApplicationActionType.CHANGE_ENROLLMENT_FILE:
      return {
        ...state,
        application: ValidEnrollmentApplication.create(
          state.application.accountNumber,
          state.application.enrollmentType,
          state.application.servicingAgentNumber.value,
          action.payload.enrollmentFile
        ),
      };
    case EnrollmentApplicationActionType.CHANGE_SERVICING_AGENT_NUMBER:
      return {
        ...state,
        application: ValidEnrollmentApplication.create(
          state.application.accountNumber,
          state.application.enrollmentType,
          action.payload.servicingAgentNumber,
          state.application.file
        ),
      };
    case EnrollmentApplicationActionType.CHANGE_ENROLLMENT_TYPE:
      return {
        ...state,
        application: ValidEnrollmentApplication.create(
          state.application.accountNumber,
          action.payload.enrollmentType,
          state.application.servicingAgentNumber.value,
          state.application.file
        ),
      };
    case EnrollmentApplicationActionType.CHANGE_ACCOUNT_NUMBER:
      return {
        ...state,
        application: ValidEnrollmentApplication.create(
          action.payload.accountNumber,
          state.application.enrollmentType,
          state.application.servicingAgentNumber.value,
          state.application.file
        ),
      };
    default:
      return state;
  }
};
